/**
 * Troubleshooting — the "check these first" page for restaurant staff.
 * Symptom-organized, non-technical, one-tap actions where possible.
 * Linked from FAQ, dashboard settings, and landing footer.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import { CaretDown, Wrench, ChatCircleText } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

const ISSUES = [
  {
    symptom: "The dashboard won't load",
    steps: [
      { title: "Check your WiFi", body: "Is another device (phone, another tablet) able to load a website? If not, the router is the problem." },
      { title: "Reboot the router", body: "Unplug the router's power for 30 seconds, plug it back in, wait 90 seconds. This fixes ~70% of \"my internet is out\" moments." },
      { title: "Try cellular data", body: "Turn WiFi OFF on your phone or tablet and let it use LTE/5G. If the dashboard loads there, your building WiFi is the culprit — call your ISP. Meanwhile you can tether your phone as a hotspot." },
      { title: "Hard refresh the page", body: "Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows/Chromebook. Forces the browser to grab a fresh copy of BuzzerAI." },
      { title: "Meanwhile — the offline cache", body: "If BuzzerAI can't reach our server, the dashboard automatically shows the last-known queue with a coral \"You're offline\" banner. You're never blind. New arrivals from the AI receptionist will appear as soon as connection returns." },
    ],
  },
  {
    symptom: "The AI isn't answering our calls",
    steps: [
      { title: "Verify forwarding is still on", body: "Call your restaurant number from a different phone. Don't answer. Does it forward to BuzzerAI in 20 seconds? If not, forwarding got turned off." },
      { title: "Re-enable conditional forwarding", body: "Verizon: dial *71 + BuzzerAI number + call. AT&T: *004* + number + #. T-Mobile: **61* + number + #. Wait for confirmation tone." },
      { title: "Never use *72 alone", body: "*72 is UNCONDITIONAL forwarding — it sends every call to the AI, your cell won't ring at all. If you accidentally set that, dial *73 to cancel, then use the conditional code above." },
      { title: "Check your Twilio balance", body: "The dashboard shows a red banner at the top when your Twilio balance is below $10. If it's zero, Twilio blocks all calls. Top up at twilio.com → Billing." },
      { title: "Check cell signal", body: "If your cell has no signal, unanswered calls can't forward. Most carriers will still forward directly to BuzzerAI when your cell is unreachable, but the behavior varies." },
    ],
  },
  {
    symptom: "Guests aren't getting SMS confirmations",
    steps: [
      { title: "Check the SMS Health banner", body: "The waitlist page shows a red banner if your Twilio SMS balance is low or if messages are failing. That's your first stop." },
      { title: "Verify the phone number", body: "Ask the guest to confirm the number they gave. Typos are the #1 cause of \"I never got the text.\" Fix it in the dashboard and hit Notify again." },
      { title: "Check if they replied STOP", body: "If a guest ever texted STOP to your number, US carriers block all future SMS to them. Only they can reverse it (reply START). We flag opt-outs in the guest row." },
      { title: "A2P 10DLC brand + campaign registration", body: "US SMS regulation requires business text senders to register a \"brand\" and \"campaign\" with Twilio. Approval takes 3–7 business days. Use our in-app A2P wizard at /dashboard/a2p to fill the form once and download a copy-paste-ready PDF for Twilio Trust Hub. Full playbook at /help/twilio-setup." },
    ],
  },
  {
    symptom: "My push alerts stopped chiming",
    steps: [
      { title: "Check notification permission", body: "In Chrome/Safari on the host phone, open the BuzzerAI site → tap the padlock or ⓘ icon in the address bar → make sure Notifications = Allow." },
      { title: "Silent mode / Do Not Disturb", body: "The alert respects your phone's silent switch and DND schedule. Check both." },
      { title: "iPhone only — installed as PWA?", body: "Apple only sends web push to sites you've added to your home screen. In Safari, tap Share → Add to Home Screen. Then open BuzzerAI from the home-screen icon (not Safari) and re-enable alerts." },
      { title: "Re-subscribe from the dashboard", body: "Waitlist tab → Locked-screen alerts card → Turn off → Enable. This creates a fresh subscription and clears stale device records." },
      { title: "Send a test", body: "Once alerts are enabled, tap \"Send test\" — it should chime within 3 seconds. If not, permission is still blocked." },
    ],
  },
  {
    symptom: "The wait times look wrong",
    steps: [
      { title: "Purge stale guests", body: "Old guests still in \"waiting\" status inflate every quote. Sweep the queue at the start of each shift — mark seated/no-show/cancelled anything from an earlier service." },
      { title: "Check your table inventory", body: "Settings → Waitlist → Table plan. Make sure the counts (2-tops, 4-tops, 6-tops) match your actual floor." },
      { title: "Check turn minutes per party size", body: "Same page — turn minutes drive the wait estimate. If a 4-top really takes 75 minutes on Fridays but you have 60 there, quotes will be low. Analytics tab shows suggested corrections based on your actual seatings." },
      { title: "Update average_wait_minutes", body: "Settings → General. This is the fallback used when smart-wait can't compute (no historical data yet). Change it to something realistic for a normal Friday." },
    ],
  },
  {
    symptom: "Guests can't find /join or scan the QR code",
    steps: [
      { title: "Verify the URL", body: "The public join page is https://queue-master-33.preview.emergentagent.com/join/YOUR-SLUG. Your slug shows on the dashboard sidebar under your business name." },
      { title: "Print a fresh QR code", body: "Dashboard → Waitlist → three-dots menu → \"Download QR code.\" QR codes fade in direct sunlight and grease — reprint every ~3 months." },
      { title: "QR code doesn't scan?", body: "Most modern phone cameras scan QR without an app. If a guest's phone doesn't, ask them to install a QR scanner app or manually type the /join URL." },
      { title: "Guest phone has no signal", body: "Some restaurants have dead zones. If cellular is spotty, offer them the guest WiFi so they can load /join on WiFi instead of cellular." },
    ],
  },
  {
    symptom: "The AI is saying something weird",
    steps: [
      { title: "Check the greeting", body: "Dashboard → Voice → \"AI Greeting.\" This is what the AI says first when it picks up. Fix typos, adjust tone, save." },
      { title: "Check business hours", body: "Settings → General → Hours. If the AI thinks you're closed but you're open, it'll route callers weirdly." },
      { title: "Check the wait time it quotes", body: "See \"The wait times look wrong\" above. The AI reads live wait quotes to callers." },
      { title: "Listen to a test call", body: "Dashboard → Voice → \"Test call\" — dial from your own phone and hear what the AI sounds like right now. Loop through until the greeting/tone feels right." },
      { title: "Review call transcripts", body: "Dashboard → Voice → any call row expands to show what the caller said and what the AI said. If a specific phrase confuses the AI repeatedly, add explicit guidance to the AI greeting." },
    ],
  },
  {
    symptom: "I got logged out / can't log in",
    steps: [
      { title: "Session expired", body: "Sessions expire after a week for security. Just log in again with your email + password." },
      { title: "Forgot password", body: "On the login page, click \"Forgot password\" — we'll email a reset link. It expires in 1 hour." },
      { title: "Wrong account", body: "If you own multiple restaurants, make sure you're logging into the right one — each business has its own account." },
      { title: "Still stuck", body: "Text or email support (see below). We can send you a magic-link login within a few minutes." },
    ],
  },
  {
    symptom: "Payment / billing issue",
    steps: [
      { title: "Card declined", body: "Dashboard → Settings → Billing → Update card. Stripe handles all payment info — we never store your card number." },
      { title: "Not sure what I was charged for", body: "Dashboard → Settings → Billing → Invoice history. Every charge is itemized." },
      { title: "Need to cancel", body: "Settings → Billing → Cancel subscription. No fees, no hoops. Your data stays available for 30 days after cancellation." },
      { title: "Refund request", body: "Email support — see below. We handle refunds case-by-case, but you'll hear back within a business day." },
    ],
  },
];

function IssueBlock({ symptom, steps, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="paper-card overflow-hidden" data-testid={`ts-issue-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 hover:bg-[#E6E2D6]/40 transition-colors"
        data-testid={`ts-toggle-${idx}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#D96C5B]/15 flex items-center justify-center shrink-0">
            <Wrench size={18} color="#D96C5B" weight="fill" />
          </div>
          <span className="font-heading font-extrabold text-lg text-[#121914]">{symptom}</span>
        </div>
        <CaretDown
          size={20}
          weight="bold"
          color="#1E3A2F"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms", flexShrink: 0 }}
        />
      </button>
      {open && (
        <ol className="px-6 pb-6 pt-4 border-t border-[#E1DFD5] space-y-4 list-decimal list-inside">
          {steps.map((s, i) => (
            <li key={s.title} className="text-sm text-[#5C6B61] leading-relaxed">
              <span className="font-semibold text-[#121914]">{s.title}. </span>
              {s.body}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function Troubleshooting() {
  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Slim top bar */}
      <div className="border-b border-[#E1DFD5] bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="ts-home-link">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
              <BuzzLogo size={22} />
            </div>
            <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/faq" className="text-[#5C6B61] hover:text-[#121914]">FAQ</Link>
            <Link to="/pricing" className="text-[#5C6B61] hover:text-[#121914]">Pricing</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <div className="overline mb-3">Support · Self-service</div>
        <h1 className="text-4xl sm:text-5xl font-heading font-extrabold tracking-tight">Something acting up?</h1>
        <p className="text-[#5C6B61] mt-4 max-w-2xl leading-relaxed">
          Try these first. Most rushes get saved in under two minutes. Find the row that sounds most like what you're seeing, tap it, and walk through the steps. If none of them work, the chat widget on the bottom right or a text to support gets a human in the loop fast.
        </p>
      </section>

      {/* Issue list */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="space-y-3">
          {ISSUES.map((iss, i) => (
            <IssueBlock key={iss.symptom} symptom={iss.symptom} steps={iss.steps} idx={i} />
          ))}
        </div>
      </section>

      {/* Escalation footer */}
      <section className="bg-[#1E3A2F] text-[#F7F6F2] px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#D96C5B]/20 border border-[#D96C5B]/40 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#F7F6F2] mb-4">
            <ChatCircleText size={14} weight="fill" /> Still stuck
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold mb-3">We're here — reach us any time.</h2>
          <p className="text-[#E1DFD5] mb-6 leading-relaxed max-w-xl mx-auto">
            Tap the chat widget on the bottom right of the landing page, or email us directly. Real humans, quick responses — no ticket queues.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#D96C5B] text-white px-5 py-2.5 font-semibold hover:bg-[#c25b4c]" data-testid="ts-cta-chat">
              Open chat →
            </Link>
            <a href="mailto:support@buzzerai.app" className="inline-flex items-center gap-2 rounded-full border border-[#F7F6F2]/30 text-[#F7F6F2] px-5 py-2.5 font-semibold hover:bg-[#F7F6F2]/10" data-testid="ts-cta-email">
              Email support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
